import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Paragraph from '../../Paragraph/Paragraph';
import SectionLoader from '../../Loader/SectionLoader';
import ModalBody from '../../Modal/ModalBody';
import { formatDate } from '../../../util/helpers/dateHelpers';
import { separateByUppercase } from '../../../util/helpers/stringHelpers';
import {
  ORDER_STATUS,
  ORDER_STATUS_TYPE,
  ORDER_STATUS_TYPES_ICONS,
  ORDER_TYPES_WITH_DESTINATION,
  ORDER_TYPES_WITH_SOURCE,
  totalPrice,
  CREATED_ON_FORMAT,
  getDestinationLabel,
  getSourceLabel,
  ORDER_TYPES_LABELS,
} from '../../../util/helpers/legacyOrderHelper';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { FETCH_LEGACY_ORDERS_LOADING } from '../../../store/actions/legacyOrders/legacyOrderActionConstants';
import themeColors from '../../../assets/styles/themeColors';
import { uFlexColumn, uTextAlignRight } from '../../../assets/styles/utility';
import LegacyOrderDetailsProductRow from '../../LegacyOrder/LegacyOrderDetailsProductRow';
import { pxToRem } from '../../../assets/styles/helper';

import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';

const OrderStateContainer = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    margin-right: ${pxToRem(8)};
    ${({ orderStateType }) =>
      orderStateType === ORDER_STATUS_TYPE.COMPLETED ||
      orderStateType === ORDER_STATUS_TYPE.PAYMENT_ISSUED ||
      orderStateType === ORDER_STATUS_TYPE.FULL_TRACKING_RECEIVED
        ? `color: ${themeColors.colorSuccess}`
        : `color: ${themeColors.colorError}}`}
  }
`;

const UpperSectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${pxToRem(16)};
`;

const UpperSectionChildrenWrapper = styled.div`
  ${uFlexColumn}
`;

const ProductAndAmountWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
`;

const ProductStyle = styled.div`
  width: 60%;
  ${({ isAlignRight }) => isAlignRight && `${uTextAlignRight}`};
`;

const SectionWrapper = styled.div`
  ${uFlexColumn}
  margin-bottom: ${pxToRem(16)};
`;

const LegacyOrderDetails = ({ legacyOrder }) => {
  const { t } = useTranslation();

  const isLoading = useSelector(
    selectIsLoadingByActionType(FETCH_LEGACY_ORDERS_LOADING),
  );

  if (!legacyOrder) {
    return null;
  }

  return (
    <SectionLoader isLoading={isLoading}>
      <ModalBody>
        <UpperSectionWrapper>
          <UpperSectionChildrenWrapper>
            <Paragraph bold>
              {t('account.legacyOrderDetails.datePlaced')}
            </Paragraph>
            <Paragraph>
              {formatDate(legacyOrder.CreatedOn, CREATED_ON_FORMAT)}
            </Paragraph>
          </UpperSectionChildrenWrapper>
          <UpperSectionChildrenWrapper>
            <Paragraph bold>
              {t('account.legacyOrderDetails.referenceNumber')}
            </Paragraph>
            <Paragraph>#{legacyOrder.OrderCode}</Paragraph>
          </UpperSectionChildrenWrapper>
        </UpperSectionWrapper>
        <SectionWrapper>
          <Paragraph fontSize={20} bold>
            {ORDER_TYPES_LABELS[legacyOrder.OrderType]}
          </Paragraph>
          <OrderStateContainer orderStateType={legacyOrder.OrderStateType}>
            {ORDER_STATUS_TYPES_ICONS[legacyOrder.OrderStateType]}
            <Paragraph color={themeColors.colorTextSecondary} bold>
              {ORDER_STATUS[legacyOrder.OrderStateType]}
            </Paragraph>
          </OrderStateContainer>
        </SectionWrapper>
        {ORDER_TYPES_WITH_SOURCE.includes(legacyOrder.OrderType) &&
          !!legacyOrder.DeliveryCity && (
            <SectionWrapper>
              <Paragraph bold>
                {t('account.legacyOrderDetails.source')}
              </Paragraph>
              <Paragraph>
                {getSourceLabel({
                  orderType: legacyOrder.OrderType,
                  storageLocation: legacyOrder.StorageLocation,
                  storageType: legacyOrder.StorageType,
                  deliveryName: legacyOrder.DeliveryName,
                  addressLine1: legacyOrder.DeliveryStreet1,
                  city: legacyOrder.DeliveryCity,
                  provinceCaption: legacyOrder.DeliveryStateProvinceCaption,
                  postalCode: legacyOrder.DeliveryPostalCode,
                  orderItems: legacyOrder.OrderItems,
                })}
              </Paragraph>
            </SectionWrapper>
          )}
        {ORDER_TYPES_WITH_DESTINATION.includes(legacyOrder.OrderType) &&
          !!legacyOrder.DeliveryCity && (
            <SectionWrapper>
              <Paragraph bold>
                {t('account.legacyOrderDetails.destination')}
              </Paragraph>
              <Paragraph>
                {getDestinationLabel({
                  orderType: legacyOrder.OrderType,
                  storageLocation: legacyOrder.StorageLocation ?? '',
                  storageType: legacyOrder.StorageType ?? '',
                  deliveryName: legacyOrder.DeliveryName ?? '',
                  addressLine1: legacyOrder.DeliveryStreet1 ?? '',
                  city: legacyOrder.DeliveryCity ?? '',
                  provinceCaption:
                    legacyOrder.DeliveryStateProvinceCaption ?? '',
                  postalCode: legacyOrder.DeliveryPostalCode ?? '',
                })}
              </Paragraph>
            </SectionWrapper>
          )}
        <SectionWrapper>
          <Paragraph marginBottom={10} bold>
            {t('account.legacyOrderDetails.orderDetails')}
          </Paragraph>
          <div>
            {legacyOrder.OrderItems.map((orderItem) => (
              <LegacyOrderDetailsProductRow orderItem={orderItem} />
            ))}
            <ProductAndAmountWrapper>
              <ProductStyle isAlignRight>
                <Paragraph>
                  {t('account.legacyOrderDetails.subtotal')}
                </Paragraph>
              </ProductStyle>
              <Paragraph>
                {formatMoneyNumeral(legacyOrder.MetalSubtotal)}
              </Paragraph>
            </ProductAndAmountWrapper>
            {(legacyOrder.DeliveryFee || legacyOrder.VaultShipping > 0) && (
              <ProductAndAmountWrapper>
                <ProductStyle isAlignRight>
                  <Paragraph>
                    {t('account.legacyOrderDetails.shipping')}
                  </Paragraph>
                </ProductStyle>
                {legacyOrder.DeliveryFee && (
                  <Paragraph>
                    {formatMoneyNumeral(legacyOrder.DeliveryFee)}
                  </Paragraph>
                )}
                {legacyOrder.VaultShipping && (
                  <Paragraph>
                    {formatMoneyNumeral(legacyOrder.VaultShipping)}
                  </Paragraph>
                )}
              </ProductAndAmountWrapper>
            )}

            {legacyOrder.WireFee && (
              <ProductAndAmountWrapper>
                <ProductStyle isAlignRight>
                  <Paragraph>
                    {t('account.legacyOrderDetails.wireFee')}
                  </Paragraph>
                </ProductStyle>
                <Paragraph>{formatMoneyNumeral(legacyOrder.WireFee)}</Paragraph>
              </ProductAndAmountWrapper>
            )}
            {legacyOrder.SalesTax > 0 && (
              <ProductAndAmountWrapper>
                <ProductStyle isAlignRight>
                  <Paragraph>
                    {t('account.legacyOrderDetails.salesTax')}
                  </Paragraph>
                </ProductStyle>
                <Paragraph>
                  {formatMoneyNumeral(legacyOrder.SalesTax)}
                </Paragraph>
              </ProductAndAmountWrapper>
            )}

            <ProductAndAmountWrapper>
              <ProductStyle isAlignRight>
                <Paragraph bold>
                  {t('account.legacyOrderDetails.orderTotal')}
                </Paragraph>
              </ProductStyle>
              <Paragraph bold>
                {formatMoneyNumeral(
                  totalPrice(
                    legacyOrder.Total,
                    legacyOrder.VaultShipping,
                    legacyOrder.WireFee,
                    legacyOrder.OrderType,
                  ),
                )}
              </Paragraph>
            </ProductAndAmountWrapper>
          </div>
        </SectionWrapper>
        <SectionWrapper>
          <Paragraph bold>
            {t('account.legacyOrderDetails.paymentMethod')}
          </Paragraph>
          <Paragraph>{separateByUppercase(legacyOrder.PaymentType)}</Paragraph>
        </SectionWrapper>
        <SectionWrapper>
          {legacyOrder.TotalRefunded && (
            <>
              <Paragraph bold>
                {t('account.legacyOrderDetails.refundAmount')}
              </Paragraph>
              <Paragraph>
                {formatMoneyNumeral(legacyOrder.TotalRefunded)}
              </Paragraph>
            </>
          )}
        </SectionWrapper>
        <SectionWrapper>
          {legacyOrder.MarketLossAmount && (
            <>
              <Paragraph bold>
                {t('account.legacyOrderDetails.marketLossCharge')}
              </Paragraph>
              <Paragraph>
                {formatMoneyNumeral(legacyOrder.MarketLossAmount)}
              </Paragraph>
            </>
          )}
        </SectionWrapper>
      </ModalBody>
    </SectionLoader>
  );
};

LegacyOrderDetails.propTypes = {
  legacyOrder: PropTypes.shape({
    CreatedOn: PropTypes.string,
    OrderCode: PropTypes.string,
    OrderTotal: PropTypes.string,
    OrderType: PropTypes.string,
    OrderStateType: PropTypes.string,
    StorageLocation: PropTypes.string,
    DeliveryStreet1: PropTypes.string,
    OrderItems: PropTypes.arrayOf(
      PropTypes.shape({
        ProductCode: PropTypes.string,
        Quantity: PropTypes.number,
        ProductCaption: PropTypes.string,
      }),
    ),
    DeliveryName: PropTypes.string,
    DeliveryCity: PropTypes.string,
    DeliveryStateProvinceCaption: PropTypes.string,
    DeliveryStateProvinceCode: PropTypes.string,
    DeliveryPostalCode: PropTypes.string,
    Total: PropTypes.number,
    StorageType: PropTypes.string,
    VaultShipping: PropTypes.number,
    WireFee: PropTypes.number,
    ProductCode: PropTypes.string,
    SalesTax: PropTypes.number,
    PaymentType: PropTypes.string,
    MetalSubtotal: PropTypes.number,
    DeliveryFee: PropTypes.string,
    TotalRefunded: PropTypes.string,
    MarketLossAmount: PropTypes.string,
  }),
};

export default LegacyOrderDetails;
